import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import {
  ChevronLeft,
  ChevronRight,
  Copy,
  Eye,
  Layers,
  LayoutGrid,
  Plus,
  Save,
  Settings,
  Trash2,
  ArrowLeft,
  Loader,
} from "lucide-react";
import { useState } from "react";
import {
  type Slide,
  defaultTheme,
} from "@/routes/~dashboard/~presentation/presentation-data";
import SlideEditor from "@/routes/~dashboard/~presentation/slide-editor";
import SlidePreview from "@/routes/~dashboard/~presentation/slide-preview";
import SlidesPanel from "@/routes/~dashboard/~presentation/slides-panel";
import StylesPanel from "@/routes/~dashboard/~presentation/styles-panel";
import SettingsPanel from "@/routes/~dashboard/~presentation/settings-panel";
import { toast } from "sonner";
import createAxios from "@/lib/axios";

export const Route = createFileRoute("/dashboard/presentation/$id")({
  component: PresentationEditor,
  loader: async (id) => {
    try {
      const client = await createAxios();
      const { data } = await client.get(
        `${import.meta.env.VITE_BACKEND_URL}/presentation/${id.params.id}`,
      );
      console.log("ID: ", id.params.id);
      console.log("Loaded presentation data:", data);
      return data;
    } catch (error) {
      console.error("Failed to load presentation:", error);
      toast.error("Failed to load presentation");
      throw error;
    }
  },
});

// Professional theme as default theme
const professionalTheme = {
  name: "Professional",
  slideBackground: "#ffffff",
  titleColor: "#1a1a1a",
  textColor: "#333333",
  accentColor: "#3b82f6",
  fontFamily: "'Inter', sans-serif",
};

type PresentationData = {
  title: string;
  subject: string;
  author: string;
  theme: string;
  slides: Record<string, unknown>[];
};

const prepareSlidesWithIds = (data: PresentationData): Slide[] => {
  return data.slides.map((slide, index: number) => ({
    id: `slide-${index + 1}`,
    ...slide,
  })) as Slide[];
};

function PresentationEditor() {
  const navigate = useNavigate();
  const presentation = Route.useLoaderData();
  console.log("Presentation data:", presentation);

  const [presentationData, setPresentationData] = useState(
    presentation ? presentation : { slides: [], title: "" },
  );
  const [slides, setSlides] = useState<Slide[]>(() =>
    presentation ? prepareSlidesWithIds(presentation as PresentationData) : [],
  );
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [theme, setTheme] = useState({
    ...defaultTheme,
    ...professionalTheme,
    presentationName: presentation?.name || "Untitled Presentation",
  });
  const [isPreviewMode, setIsPreviewMode] = useState(false);

  if (!presentation) {
    return <div>Failed to load presentation</div>;
  }

  const currentSlide = slides[currentSlideIndex];

  const updateTheme = (updatedTheme: typeof theme) => {
    const safeTheme = {
      ...updatedTheme,
      presentationName:
        updatedTheme.presentationName || "Untitled Presentation",
    };

    setTheme(safeTheme);

    const updatedSlides = slides.map((slide) => {
      return {
        ...slide,
        background: safeTheme.slideBackground,
        titleColor: safeTheme.titleColor,
        textColor: safeTheme.textColor,
      };
    });

    setSlides(updatedSlides);
  };
  const addNewSlide = () => {
    const newSlide: Slide = {
      id: `slide-${Date.now()}`,
      title: "New Slide",
      content: "Add your content here",
      layout: "contentFull",
      background: theme.slideBackground,
      titleColor: theme.titleColor,
      textColor: theme.textColor,
    };

    setSlides([...slides, newSlide]);
    setCurrentSlideIndex(slides.length);

    setPresentationData({
      ...presentationData,
      slides: [
        ...presentationData.slides,
        {
          title: "New Slide",
          subtitle: "Slide subtitle",
          content: "Add your content here",
          layout: "contentFull",
        },
      ],
    });
  };
  const duplicateSlide = () => {
    const duplicatedSlide = {
      ...currentSlide,
      id: `slide-${Date.now()}`,
    };

    const newSlides = [...slides];
    newSlides.splice(currentSlideIndex + 1, 0, duplicatedSlide);
    setSlides(newSlides);
    setCurrentSlideIndex(currentSlideIndex + 1);

    const newPresentationSlides = [...presentationData.slides];
    newPresentationSlides.splice(currentSlideIndex + 1, 0, {
      ...presentationData.slides[currentSlideIndex],
    });
    setPresentationData({
      ...presentationData,
      slides: newPresentationSlides,
    });
  };

  const deleteSlide = () => {
    if (slides.length <= 1) return;

    const newSlides = slides.filter((_, index) => index !== currentSlideIndex);
    setSlides(newSlides);
    setCurrentSlideIndex(Math.min(currentSlideIndex, newSlides.length - 1));

    const newPresentationSlides = presentationData.slides.filter(
      (_: unknown, index: number) => index !== currentSlideIndex,
    );
    setPresentationData({
      ...presentationData,
      slides: newPresentationSlides,
    });
  };
  const updateCurrentSlide = (updatedSlide: Slide) => {
    const newSlides = [...slides];
    newSlides[currentSlideIndex] = updatedSlide;
    setSlides(newSlides);

    const newPresentationSlides = [...presentationData.slides];
    const { ...slideForData } = updatedSlide;
    newPresentationSlides[currentSlideIndex] =
      slideForData as (typeof presentationData.slides)[0];
    setPresentationData({
      ...presentationData,
      slides: newPresentationSlides,
    });
  };

  const navigateSlides = (direction: "prev" | "next") => {
    if (direction === "prev" && currentSlideIndex > 0) {
      setCurrentSlideIndex(currentSlideIndex - 1);
    } else if (direction === "next" && currentSlideIndex < slides.length - 1) {
      setCurrentSlideIndex(currentSlideIndex + 1);
    }
  };
  const handleSave = async () => {
    const optimizedSlides = slides.map((slide, index) => {
      const slideData = presentationData.slides[index];

      const style: Record<string, string> = {};

      if (slide.background && slide.background !== theme.slideBackground) {
        style.background = slide.background;
      }

      if (slide.titleColor && slide.titleColor !== theme.titleColor) {
        style.titleColor = slide.titleColor;
      }

      if (slide.textColor && slide.textColor !== theme.textColor) {
        style.textColor = slide.textColor;
      }
      const optimizedSlide: Record<string, unknown> = { ...slideData };

      if ("id" in optimizedSlide) {
        delete optimizedSlide.id;
      }

      if (Object.keys(style).length > 0) {
        optimizedSlide.style = style;
      }

      return optimizedSlide;
    });

    const optimizedData = {
      pptId: presentation._id,
      userId: presentation.userId,
      metadata: {
        title: theme.presentationName || "Untitled Presentation",
        author: presentationData.author || "Edunova",
        subject: presentationData.subject || "Presentation",
        company: "Edunova",
      },
      theme: {
        global: {
          background: theme.slideBackground,
          titleColor: theme.titleColor,
          textColor: theme.textColor,
          accentColor: theme.accentColor,
          fontFamily: theme.fontFamily,
          transition: theme.transition,
        },
      },
      slides: optimizedSlides,
    };

    console.log("Saving presentation:", optimizedData);
    try {
      toast.loading("Sending presentation to server...");

      const client = await createAxios();

      const response = await client.post(
        `${import.meta.env.PPTX_GENJS_URL}/create`,
        optimizedData,
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      toast.success("Presentation saved successfully!");
      navigate({
        to: `/dashboard/presentation/${response.data.id}/view`,
      });

      return response.data;
    } catch (error) {
      toast.error("Failed to save presentation");
      throw error;
    }
  };

  const handleBackNavigation = () => {
    navigate({ to: "/dashboard/presentation" });
  };

  return presentation.status === "completed" ? (
    <div className="flex h-screen flex-col bg-white">
      {/* Header */}
      <header className="bg-background text-white">
        <div className="flex h-16 items-center justify-between px-4">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              className="text-secondary-foreground hover:bg-secondary hover:text-white"
              onClick={handleBackNavigation}
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <h1 className="bg-clip-text text-xl font-semibold text-transparent text-white">
              {theme.presentationName || "My Presentation"}
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsPreviewMode(!isPreviewMode)}
              className="border border-secondary bg-amber-100 text-black hover:bg-amber-200 hover:text-black"
            >
              {isPreviewMode ? "Edit Mode" : "Preview Mode"}
              {isPreviewMode ? null : <Eye className="ml-2 h-4 w-4" />}
            </Button>
            <Button
              variant="default"
              size="sm"
              onClick={handleSave}
              className="bg-green-700 text-white hover:bg-green-900"
            >
              <Save className="mr-2 h-4 w-4" />
              Save
            </Button>
          </div>
        </div>
      </header>

      {isPreviewMode ? (
        <div className="flex-1 overflow-hidden bg-background p-8">
          <SlidePreview
            slides={slides}
            currentIndex={currentSlideIndex}
            setCurrentIndex={setCurrentSlideIndex}
            theme={theme}
          />
        </div>
      ) : (
        <div className="flex flex-1 gap-4 overflow-hidden bg-background">
          {/* Left Sidebar */}
          <div className="hidden w-80 overflow-y-auto bg-background text-black md:block">
            <Tabs defaultValue="slides" className="w-full">
              <TabsList className="grid w-full grid-cols-3 bg-secondary">
                <TabsTrigger
                  value="slides"
                  className="data-[state=active]:bg-accent"
                >
                  <Layers className="mr-1 h-3 w-3" />
                  Slides
                </TabsTrigger>
                <TabsTrigger
                  value="styles"
                  className="data-[state=active]:bg-accent"
                >
                  <LayoutGrid className="mr-1 h-3 w-3" />
                  Styles
                </TabsTrigger>
                <TabsTrigger
                  value="settings"
                  className="data-[state=active]:bg-accent"
                >
                  <Settings className="mr-1 h-3 w-3" />
                  Themes
                </TabsTrigger>
              </TabsList>
              <TabsContent
                value="slides"
                className="mt-0 border-0 p-0 text-red-400"
              >
                <SlidesPanel
                  slides={slides}
                  currentIndex={currentSlideIndex}
                  onSelectSlide={setCurrentSlideIndex}
                  onAddSlide={addNewSlide}
                  theme={theme}
                />
              </TabsContent>
              <TabsContent value="styles" className="mt-0 border-0 p-0">
                <StylesPanel
                  theme={theme}
                  setTheme={(updatedTheme) => {
                    updateTheme({
                      ...updatedTheme,
                      presentationName:
                        updatedTheme.presentationName || theme.presentationName,
                      name: "",
                    });
                  }}
                  currentSlide={currentSlide}
                  updateCurrentSlide={updateCurrentSlide}
                  allSlides={slides}
                  updateAllSlides={setSlides}
                />
              </TabsContent>
              <TabsContent value="settings" className="mt-0 border-0 p-0">
                <SettingsPanel
                  theme={theme}
                  setTheme={(updatedTheme) => {
                    updateTheme({
                      ...updatedTheme,
                      presentationName:
                        updatedTheme.presentationName ||
                        "Untitled Presentation",
                      name: "",
                    });
                  }}
                />
              </TabsContent>
            </Tabs>
          </div>

          {/* Main Editor Area */}
          <div className="flex flex-1 flex-col overflow-hidden">
            {/* Toolbar */}
            <div className="flex items-center justify-between bg-secondary p-3 text-white">
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => navigateSlides("prev")}
                  className="hover:bg-accent"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="rounded-md bg-accent px-3 py-1 text-sm font-medium">
                  Slide {currentSlideIndex + 1} of {slides.length}
                </span>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => navigateSlides("next")}
                  className="hover:bg-accent"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="default"
                  size="sm"
                  onClick={addNewSlide}
                  className="border border-secondary hover:bg-accent hover:text-white"
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Add Slide
                </Button>
                <Button
                  variant="default"
                  size="sm"
                  onClick={duplicateSlide}
                  className="border border-secondary hover:bg-accent hover:text-white"
                >
                  <Copy className="mr-2 h-4 w-4" />
                  Duplicate
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={deleteSlide}
                  disabled={slides.length <= 1}
                  className="border border-secondary hover:bg-red-950 hover:text-white"
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete
                </Button>
              </div>
            </div>

            {/* Editor Canvas */}
            <div className="flex flex-1 items-start justify-center overflow-auto border-b border-l border-r border-secondary bg-background p-8">
              <SlideEditor
                slide={currentSlide}
                updateSlide={updateCurrentSlide}
                theme={theme}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  ) : (
    <div className="flex h-screen items-center justify-center bg-gray-100 text-gray-700">
      <div className="text-center">
        <Loader className="inline-block size-10 animate-pulse" />

        <h1 className="mb-4 mt-4 text-4xl font-bold">
          Generating your presentation
        </h1>
        <p className="mb-4">
          This may take a few minutes. Please wait while we prepare your
          presentation.
        </p>
        <Button
          variant="secondary"
          className="mt-4"
          onClick={() => navigate({ to: "/dashboard" })}
        >
          Go Back to Dashboard
        </Button>
      </div>
    </div>
  );
}
